import { useState, useMemo, useCallback } from 'react';

const GRID_SIZE = 9;
const BOX_SIZE = 3;

const getBoxStart = (index) => Math.floor(index / BOX_SIZE) * BOX_SIZE;

export function useCellSelection(grid) {
  const [selectedCell, setSelectedCell] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [focusIntensity, setFocusIntensity] = useState(0.7);

  const relatedSet = useMemo(() => {
    if (!selectedCell) return new Set();
    
    const [row, col] = selectedCell;
    const related = new Set();
    related.add(`${row}-${col}`);
    
    for (let i = 0; i < GRID_SIZE; i++) {
      related.add(`${row}-${i}`);
      related.add(`${i}-${col}`);
    }
    
    const boxRow = getBoxStart(row);
    const boxCol = getBoxStart(col);
    for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
      for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
        related.add(`${r}-${c}`);
      }
    }
    
    return related;
  }, [selectedCell]);

  const relatedCells = useMemo(() => {
    if (!selectedCell) return [];
    const [row, col] = selectedCell;
    return Array.from(relatedSet)
      .filter(key => key !== `${row}-${col}`)
      .map(key => {
        const [r, c] = key.split('-').map(Number);
        return [r, c];
      });
  }, [selectedCell, relatedSet]);

  const sameNumberCells = useMemo(() => {
    if (!selectedCell) return [];
    
    const [row, col] = selectedCell;
    const value = grid[row]?.[col];
    if (!value || value === 0) return [];
    
    const cells = [];
    for (let r = 0; r < GRID_SIZE; r++) {
      for (let c = 0; c < GRID_SIZE; c++) {
        if (grid[r][c] === value && (r !== row || c !== col)) {
          cells.push([r, c]);
        }
      }
    }
    return cells;
  }, [selectedCell, grid]);

  const selectCell = useCallback((row, col) => {
    setSelectedCell(prev => {
      if (prev && prev[0] === row && prev[1] === col) {
        return null;
      }
      return [row, col];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCell(null);
  }, []);

  const moveSelection = useCallback((direction) => {
    setSelectedCell(current => {
      if (!current) return current;
      const [row, col] = current;

      switch (direction) {
        case 'up': return row > 0 ? [row - 1, col] : current;
        case 'down': return row < GRID_SIZE - 1 ? [row + 1, col] : current;
        case 'left': return col > 0 ? [row, col - 1] : current;
        case 'right': return col < GRID_SIZE - 1 ? [row, col + 1] : current;
        default: return current;
      }
    });
  }, []);

  const isSelected = useCallback((row, col) => {
    return selectedCell !== null && selectedCell[0] === row && selectedCell[1] === col;
  }, [selectedCell]);

  const isRelated = useCallback((row, col) => {
    return relatedSet.has(`${row}-${col}`) && !isSelected(row, col);
  }, [relatedSet, isSelected]);

  const isSameNumber = useCallback((row, col) => {
    return sameNumberCells.some(([r, c]) => r === row && c === col);
  }, [sameNumberCells]);

  const isUnrelated = useCallback((row, col) => {
    if (!selectedCell || !focusMode) return false;
    return !relatedSet.has(`${row}-${col}`);
  }, [selectedCell, focusMode, relatedSet]);

  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => !prev);
  }, []);

  return {
    selectedCell,
    selectCell,
    clearSelection,
    moveSelection,
    isSelected,
    isRelated,
    isSameNumber,
    isUnrelated,
    focusMode,
    focusIntensity,
    setFocusIntensity,
    toggleFocusMode,
  };
}
